import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, query, where, getDocs, updateDoc, doc,
  getDoc, deleteDoc
} from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, CircularProgress,
  Alert, Button, Checkbox, FormControlLabel, Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';

export default function JoinPage() {
  const { token } = useParams();
  const { currentUser, loginWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [gdprChecked, setGdprChecked] = useState(false);
  const [savingConsent, setSavingConsent] = useState(false);
  const [pendingInvitation, setPendingInvitation] = useState(null);

  useEffect(() => {
    if (!token) { setStatus('error'); setMessage('Ogiltig länk.'); return; }
    if (!currentUser) { setStatus('needsLogin'); return; }
    processInvitation();
  }, [currentUser, token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function processInvitation() {
    setStatus('loading');
    try {
      const snap = await getDocs(
        query(collection(db, 'invitations'),
          where('token', '==', token),
          where('status', '==', 'pending'))
      );

      if (snap.empty) {
        setStatus('error');
        setMessage('Inbjudningslänken är ogiltig eller har redan använts.');
        return;
      }

      const invitation = snap.docs[0];
      const data = invitation.data();

      if (data.expiresAt?.toDate && data.expiresAt.toDate() < new Date()) {
        setStatus('error');
        setMessage('Inbjudningslänken har gått ut. Be om en ny länk.');
        return;
      }

      // Kolla om användaren redan godkänt GDPR
      const userSnap = await getDocs(
        query(collection(db, 'users'),
          where('__name__', '==', currentUser.uid))
      );
      const userData = userSnap.docs[0]?.data();
      const hasConsent = userData?.gdprConsent === true;

      if (hasConsent) {
        // Redan godkänt – koppla direkt
        await linkUser(invitation.id, data);
      } else {
        // Visa GDPR-samtycke först
        setPendingInvitation({ id: invitation.id, data });
        setStatus('gdpr');
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
      setMessage('Något gick fel. Försök igen eller kontakta din administratör.');
    }
  }

  async function linkUser(invitationId, data) {
    const updates = {
      departmentId: data.departmentId,
      companyId: data.companyId,
      updatedAt: new Date()
    };

    // Auto-applicera väntande registeruppgifter om de finns
    if (currentUser.email) {
      const pendingSnap = await getDoc(
        doc(db, 'pendingEmployees', currentUser.email.toLowerCase())
      );
      if (pendingSnap.exists()) {
        const pending = pendingSnap.data();
        const { email: _e, createdAt: _c, importedAt: _i, ...fields } = pending;
        Object.assign(updates, fields);
        // Ta bort pending-posten
        await deleteDoc(doc(db, 'pendingEmployees', currentUser.email.toLowerCase()));
      }
    }

    await updateDoc(doc(db, 'users', currentUser.uid), updates);
    await updateDoc(doc(db, 'invitations', invitationId), {
      status: 'accepted',
      acceptedBy: currentUser.uid,
      acceptedAt: new Date()
    });
    setStatus('success');
    setMessage('Du har blivit kopplad till affärsområdet!');
    setTimeout(() => navigate('/dashboard'), 2500);
  }

  async function handleAcceptGDPR() {
    if (!gdprChecked || !pendingInvitation) return;
    setSavingConsent(true);
    try {
      // Spara GDPR-samtycke
      await updateDoc(doc(db, 'users', currentUser.uid), {
        gdprConsent: true,
        gdprConsentDate: new Date(),
        gdprConsentVersion: '1.0'
      });
      // Koppla användaren
      await linkUser(pendingInvitation.id, pendingInvitation.data);
    } catch (err) {
      console.error(err);
      setStatus('error');
      setMessage('Något gick fel. Försök igen.');
    } finally {
      setSavingConsent(false);
    }
  }

  async function handleLogin() {
    try {
      await loginWithMicrosoft();
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      py: 4
    }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography fontWeight={900} sx={{
            fontSize: '2.5rem', color: 'white', letterSpacing: '-1px',
            fontFamily: '"Segoe UI", "Roboto", "Arial", sans-serif'
          }}>
            Lagbas<Box component="span" sx={{ color: '#FF6B00' }}>N</Box>
          </Typography>
        </Box>

        <Paper elevation={10} sx={{
          p: 5, borderRadius: 3, textAlign: 'center',
          bgcolor: '#1a1a1a', border: '1px solid #2a2a2a'
        }}>

          {/* LADDAR */}
          {status === 'loading' && (
            <Box>
              <CircularProgress sx={{ color: '#FF6B00', mb: 2 }} />
              <Typography sx={{ color: 'white' }}>
                Behandlar inbjudan...
              </Typography>
            </Box>
          )}

          {/* BEHÖVER LOGGA IN */}
          {status === 'needsLogin' && (
            <Box>
              <Typography variant="h6" fontWeight="bold"
                sx={{ color: 'white', mb: 1 }}>
                Du har blivit inbjuden!
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', mb: 3 }}>
                Logga in med ditt Office 365-konto för att
                acceptera inbjudan.
              </Typography>
              <Button variant="contained" size="large" fullWidth
                onClick={handleLogin}
                sx={{ bgcolor: 'white', color: '#0a0a0a', fontWeight: 'bold',
                  py: 1.5, '&:hover': { bgcolor: '#e0e0e0' } }}>
                Logga in med Microsoft 365
              </Button>
            </Box>
          )}

          {/* GDPR-SAMTYCKE */}
          {status === 'gdpr' && (
            <Box sx={{ textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
                mb: 2, justifyContent: 'center' }}>
                <GppMaybeIcon sx={{ color: '#FF6B00', fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold"
                  sx={{ color: 'white' }}>
                  Godkänn personuppgiftshantering
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: '#aaa', mb: 2,
                textAlign: 'center' }}>
                Innan du får tillgång till LagbasN behöver vi ditt
                samtycke till hur vi hanterar dina personuppgifter
                (GDPR).
              </Typography>

              <Divider sx={{ borderColor: '#333', mb: 2 }} />

              <Typography variant="subtitle2" fontWeight="bold"
                sx={{ color: '#ccc', mb: 0.5 }}>
                Vilka uppgifter lagrar vi?
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2,
                color: '#888' }}>
                {[
                  'Namn och e-post (från ditt Microsoft 365-konto)',
                  'Yrkesuppgifter: titel, uppdrag, utbildning, kompetenser',
                  'CV-information för konsultmatchning och anbudsarbete',
                ].map((item, i) => (
                  <li key={i}>
                    <Typography variant="body2" sx={{ color: '#888' }}>
                      {item}
                    </Typography>
                  </li>
                ))}
              </Box>

              <Typography variant="subtitle2" fontWeight="bold"
                sx={{ color: '#ccc', mb: 0.5 }}>
                Hur använder vi uppgifterna?
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2 }}>
                {[
                  'Intern konsultmatchning för anbud inom er organisation',
                  'Generering av CV och konsultprofiler',
                  'Administration av anbudsteam',
                ].map((item, i) => (
                  <li key={i}>
                    <Typography variant="body2" sx={{ color: '#888' }}>
                      {item}
                    </Typography>
                  </li>
                ))}
              </Box>

              <Typography variant="subtitle2" fontWeight="bold"
                sx={{ color: '#ccc', mb: 0.5 }}>
                Var lagras data?
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                Google Firebase inom EU (europe-north1, Finland).
                Data delas inte med tredje part.
              </Typography>

              <Typography variant="subtitle2" fontWeight="bold"
                sx={{ color: '#ccc', mb: 0.5 }}>
                Dina rättigheter
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                Du har rätt att se, rätta och begära radering av
                din data. Kontakta din administratör för att
                utöva dina rättigheter.
              </Typography>

              <Divider sx={{ borderColor: '#333', mb: 2 }} />

              <Box sx={{
                p: 2, bgcolor: '#0f0f1a', borderRadius: 2,
                border: '1px solid #3949ab', mb: 3
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={gdprChecked}
                      onChange={e => setGdprChecked(e.target.checked)}
                      sx={{ color: '#3949ab',
                        '&.Mui-checked': { color: '#FF6B00' } }}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight="bold"
                      sx={{ color: 'white' }}>
                      Jag godkänner att LagbasN behandlar mina
                      personuppgifter i enlighet med GDPR och
                      beskrivningen ovan.
                    </Typography>
                  }
                />
              </Box>

              <Button
                variant="contained" fullWidth size="large"
                disabled={!gdprChecked || savingConsent}
                startIcon={savingConsent
                  ? <CircularProgress size={16} color="inherit" />
                  : <GppMaybeIcon />}
                onClick={handleAcceptGDPR}
                sx={{
                  bgcolor: gdprChecked ? '#FF6B00' : '#333',
                  color: 'white', fontWeight: 'bold', py: 1.5,
                  '&:hover': { bgcolor: gdprChecked ? '#e65c00' : '#333' },
                  '&.Mui-disabled': { bgcolor: '#333', color: '#555' }
                }}>
                {savingConsent
                  ? 'Sparar...'
                  : 'Jag godkänner – Gå vidare'}
              </Button>
            </Box>
          )}

          {/* LYCKADES */}
          {status === 'success' && (
            <Box>
              <CheckCircleIcon sx={{ fontSize: 60, color: '#2e7d32', mb: 2 }} />
              <Typography variant="h6" fontWeight="bold"
                sx={{ color: 'white', mb: 1 }}>
                Välkommen!
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                {message}
              </Typography>
              <Typography variant="caption" sx={{ color: '#555' }}>
                Omdirigerar till dashboarden...
              </Typography>
            </Box>
          )}

          {/* FEL */}
          {status === 'error' && (
            <Box>
              <ErrorIcon sx={{ fontSize: 60, color: '#d32f2f', mb: 2 }} />
              <Typography variant="h6" fontWeight="bold"
                sx={{ color: 'white', mb: 1 }}>
                Något gick fel
              </Typography>
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                {message}
              </Alert>
              <Button variant="outlined" onClick={() => navigate('/')}
                sx={{ color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)' }}>
                Tillbaka till startsidan
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}